import React, { useState, useRef } from 'react';
import { transcribeAudio } from '../services/whisperApi';
import { scoreFullReading } from '../services/claudeApi';
import { convertForDisplay } from '../utils/kanjiConverter';
import { saveRecord } from '../utils/storage';
import { saveAudio } from '../utils/audioStorage';
import { createRecorder, getRecordingMimeType } from '../utils/audioRecording';
import { saveLog } from '../services/readingLogsApi';
import { uploadAudio } from '../services/audioStorageApi';
import { trackEvent } from '../utils/analytics';
import { canUseAIScore, incrementAIScoreUsage, getRemainingAIScore, shouldUseCredit, getAIScoreLimit } from '../utils/premiumGate';

const GROUP_SIZE_LONG  = 3; // 長いテキスト：3列ごと
const GROUP_SIZE_SHORT = 2; // 短いテキスト：2列ごと

export default function HonbanMode({ text, onComplete, onBack, isPremium, isGuest, aiCredits, onUseCredit, childId, grade }) {
  const [phase, setPhase] = useState('ready'); // ready | countdown | recording | loading | done
  const [countdown, setCountdown] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const textBoxRef = useRef(null);
  const recStartRef = useRef(null);

  const scrollText = (dir) => {
    const el = textBoxRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: 'smooth' });
  };

  const lines = text.paragraphs ? text.paragraphs.flat() : (text.content ?? []);
  const fullText = lines.join('');
  const isShortText = lines.length <= 10;

  const startCountdown = () => {
    setPhase('countdown');
    let c = 3;
    setCountdown(c);
    const timer = setInterval(() => {
      c--;
      if (c === 0) {
        clearInterval(timer);
        setCountdown(null);
        startRecording();
      } else {
        setCountdown(c);
      }
    }, 1000);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = createRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = handleStop;
      mr.start();
      mediaRef.current = mr;
      recStartRef.current = Date.now();
      const paragraphCount = text.paragraphs ? text.paragraphs.length : 1;
      trackEvent('ondoku_recording_start', { mode: 'honban', text_title: text.title || '（タイトルなし）', grade: grade ?? 'unknown', paragraph_count: paragraphCount });
      setPhase('recording');
    } catch {
      alert('マイクが　つかえません。\nブラウザの　せってい　を　かくにんしてね。');
      setPhase('ready');
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    mediaRef.current?.stream.getTracks().forEach((t) => t.stop());
    setPhase('loading');
  };

  const handleStop = async () => {
    const duration_sec = recStartRef.current
      ? Math.round((Date.now() - recStartRef.current) / 1000)
      : 0;
    trackEvent('ondoku_recording_stop', { mode: 'honban', duration_sec, grade: grade ?? 'unknown' });
    try {
      const mimeType = mediaRef.current?.mimeType || getRecordingMimeType() || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      const t = await transcribeAudio(blob);
      // AI採点の回数チェック
      const dailyOk  = canUseAIScore(isPremium, isGuest);
      const creditOk = shouldUseCredit(isPremium, aiCredits, isGuest);

      if (!dailyOk && !creditOk) {
        // 日次（ゲストは生涯）上限かつクレジットなし
        const limit = getAIScoreLimit(isPremium, isGuest);
        trackEvent('ondoku_daily_limit_reached', { is_premium: isPremium, is_guest: isGuest });
        onComplete({
          mode: 'honban', title: text.title,
          totalStars: 0, avgScore: 0,
          transcript: t, display: convertForDisplay(t) || t, feedback: '',
          details: [],
          limitReached: true,
          limitMsg: isPremium
            ? `今日のAI採点は${limit}回まで。また明日チャレンジしよう！`
            : isGuest
              ? `おためしのAI採点は${limit}回まで。ログインすると毎日ちょうせんできるよ！`
              : `無料プランのAI採点は1日${limit}回まで。プレミアムにすると1日10回使えるよ！`,
        });
        return;
      }

      if (dailyOk) {
        incrementAIScoreUsage(isGuest);
      } else {
        // クレジットを1消費
        if (onUseCredit) await onUseCredit();
        trackEvent('ondoku_credit_used', { remaining: aiCredits - 1 });
      }
      trackEvent('ondoku_ai_scoring_start', { mode: 'honban', is_premium: isPremium, remaining: getRemainingAIScore(isPremium, isGuest), grade: grade ?? 'unknown' });
      const { score, stars, feedback, details, display } = await scoreFullReading(fullText, t, grade);
      trackEvent('ondoku_ai_scoring_complete', { mode: 'honban', score, grade: grade ?? 'unknown' });
      const record = {
        mode: 'honban',
        title: text.title,
        totalStars: stars,
        avgScore: score,
        transcript: t,
        display,
        feedback,
        details,
        audioUrl: url,
        date: new Date().toISOString(),
      };
      const saved = saveRecord(record, childId);
      saveAudio(saved.id, blob, isPremium);
      await saveLog(saved, childId, isPremium); // await: 他デバイスで即座に見えるよう先に確定させる
      // ログイン済み（childIdあり）なら全員クラウドに保存（無料3件・プレミアム20件）
      if (childId) {
        uploadAudio(saved.id, blob, childId, mimeType, isPremium);
      }
      onComplete({ ...record, id: saved.id });
    } catch (e) {
      console.error(e);
      setPhase('ready');
      alert(`エラー: ${e.message}`);
    }
  };

  return (
    <div className="screen honban-screen">
      <button className="btn-back" onClick={onBack} disabled={phase === 'recording'}>
        ← もどる
      </button>

      <div className="honban-header">
        <h2 className="honban-title">🎯 本番</h2>
        <p className="honban-sub">『{text.title}』</p>
      </div>

      {/* お手本テキスト（縦書き・全文） */}
      <div className={`text-box-wrap${isShortText ? ' short-wrap' : ''}`}>
        {!isShortText && (
          <button className="scroll-btn scroll-btn-right" onClick={() => scrollText(-1)}>&#8249;</button>
        )}
        <div
          className={`text-vertical-full${isShortText ? ' short-text' : ''}`}
          ref={textBoxRef}
        >
          {lines.map((line, i) => (
            <div
              key={i}
              className={`vertical-line vl-group-${Math.floor(i / (isShortText ? GROUP_SIZE_SHORT : GROUP_SIZE_LONG)) % 2}`}
            >
              {line}
            </div>
          ))}
        </div>
        {!isShortText && (
          <button className="scroll-btn scroll-btn-left" onClick={() => scrollText(1)}>&#8250;</button>
        )}
      </div>

      {/* フェーズ別UI */}
      {phase === 'ready' && (
        <div className="honban-action">
          <p className="honban-hint">よんだら　ボタンを　おしてとめてね</p>
          <button className="btn-honban-start" onClick={startCountdown}>
            🎤 はじめる！
          </button>
        </div>
      )}

      {phase === 'countdown' && (
        <div className="countdown-box">
          <div className="countdown-num">{countdown}</div>
          <p>よういして！</p>
        </div>
      )}

      {phase === 'recording' && (
        <div className="honban-action">
          <div className="recording-indicator">
            <span className="rec-dot" />
            <span>ろくおんちゅう…</span>
          </div>
          <button className="btn-honban-stop" onClick={stopRecording}>
            ⏹ よみおわった！
          </button>
        </div>
      )}

      {phase === 'loading' && (
        <div className="loading-box">
          <div className="spinner" />
          <p>さいてんちゅう…　まってね！</p>
        </div>
      )}
    </div>
  );
}
